export interface WithRetryOptions {
  operationName?: string;
  shouldRetry?: (error: unknown) => boolean;
  timeout?: number;
  retries?: number;
  retryDelay?: number;
}

/**
 * Low-level promise wrapper with retry, timeout, and predicate logic.
 * Use this for non-HTTP promises or when you need fine-grained control.
 * For HTTP requests, prefer fetchApi() or fetchApiWithLoading().
 *
 * @param operation - Function that returns a promise to retry
 * @param options - Optional configuration
 * @returns Promise resolving to the data or throwing an error
 */
export async function withRetry<T>(
  operation: () => Promise<T>,
  options?: WithRetryOptions,
): Promise<T> {
  const retries =
    options?.retries ??
    (Number(process.env.NEXT_PUBLIC_LL_WEB_MAX_RETRY_ATTEMPTS) || 3);
  const retryDelay =
    options?.retryDelay ??
    (Number(process.env.NEXT_PUBLIC_LL_WEB_BASE_DELAY_MS) || 1000);
  const { timeout, shouldRetry, operationName } = options ?? {};

  let lastError: unknown;

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      if (timeout !== undefined) {
        const timeoutPromise = new Promise<never>((_, reject) => {
          setTimeout(
            () => reject(new Error(`Request timeout after ${timeout}ms`)),
            timeout,
          );
        });
        return await Promise.race([operation(), timeoutPromise]);
      }

      return await operation();
    } catch (error) {
      lastError = error;

      if (shouldRetry !== undefined && !shouldRetry(error)) {
        break;
      }

      if (attempt === retries) {
        break;
      }

      await new Promise((resolve) =>
        setTimeout(resolve, retryDelay * (attempt + 1)),
      );
    }
  }

  if (lastError instanceof Error) {
    throw lastError;
  }
  throw new Error(
    lastError != null
      ? `${operationName != null ? `${operationName}: ` : ""}${String(lastError)}`
      : `${operationName ?? "operation"} failed without error`,
  );
}
